import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { environment } from 'src/environments/environment';

export interface WorkOrder {
  work_order_id: number;
  work_order_number: string;
  project_id: number;
  project_name: string;
  client_name: string;
  vendor_name: string;
  issue_date: string;
  total_amount: number;
  status: string;
  is_shared_with_client: boolean;
  email_sent: boolean;
  pdf_generated: boolean;
  created_at: string;
}

@Component({
  selector: 'app-work-order-list',
  templateUrl: './work-order-list.component.html',
  styleUrls: ['./work-order-list.component.css']
})
export class WorkOrderListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'work_order_number',
    'project_name',
    'client_name',
    'vendor_name',
    'issue_date',
    'total_amount',
    'status',
    'actions'
  ];

  dataSource = new MatTableDataSource<WorkOrder>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;
  searchKeyword = '';
  selectedStatus: string = '';
  selectedProject: string = '';

  // Pagination
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50];

  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'issued', label: 'Issued' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadWorkOrders();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  loadWorkOrders() {
    this.loading = true;
    const params: any = {
      page: this.pageIndex + 1,
      limit: this.pageSize
    };

    if (this.searchKeyword) {
      params.search = this.searchKeyword;
    }

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.selectedProject) {
      params.project_id = this.selectedProject;
    }

    this.apiService.getWorkOrders(params).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.dataSource.data = res.data;
          this.totalItems = res.pagination?.total || 0;
        } else {
          this.toastrService.error('Failed to load work orders');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading work orders:', err);
        this.toastrService.error(err.error?.error || 'Failed to load work orders');
        this.loading = false;
      }
    });
  }

  applyFilter() {
    this.pageIndex = 0;
    this.loadWorkOrders();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadWorkOrders();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'draft': 'bg-gray-100 text-gray-800',
      'issued': 'bg-blue-100 text-blue-800',
      'acknowledged': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  viewDetails(workOrder: WorkOrder) {
    this.router.navigate(['/home/work-orders', workOrder.work_order_id]);
  }

  editWorkOrder(workOrder: WorkOrder) {
    this.router.navigate(['/home/work-orders', workOrder.work_order_id, 'edit']);
  }

  deleteWorkOrder(workOrder: WorkOrder) {
    if (confirm(`Are you sure you want to delete work order "${workOrder.work_order_number}"?`)) {
      this.apiService.deleteWorkOrder(workOrder.work_order_id).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('Work order deleted successfully');
            this.loadWorkOrders();
          }
        },
        error: (err: any) => {
          console.error('Error deleting work order:', err);
          this.toastrService.error(err.error?.error || 'Failed to delete work order');
        }
      });
    }
  }

  createNew() {
    this.router.navigate(['/home/work-orders/new']);
  }

  generatePDF(workOrder: WorkOrder) {
    this.apiService.generateWorkOrderPDF(workOrder.work_order_id).subscribe({
      next: (res: any) => {
        if (res.success) {
          window.open(`${environment.BASE_URL}work-orders/${workOrder.work_order_id}/pdf`, '_blank');
        }
      },
      error: (err: any) => {
        console.error('Error generating PDF:', err);
        this.toastrService.error('Failed to generate PDF');
      }
    });
  }

  shareWithClient(workOrder: WorkOrder) {
    this.apiService.shareWorkOrderWithClient(workOrder.work_order_id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Work order shared with client');
          const shareableLink = res.data.shareable_link;
          // Copy to clipboard or show dialog
          navigator.clipboard.writeText(shareableLink).then(() => {
            alert(`Shareable link copied to clipboard:\n${shareableLink}`);
          });
          this.loadWorkOrders();
        }
      },
      error: (err: any) => {
        console.error('Error sharing work order:', err);
        this.toastrService.error(err.error?.error || 'Failed to share work order');
      }
    });
  }

  sendEmail(workOrder: WorkOrder) {
    // Open email dialog
    const emailData = {
      to: workOrder.vendor_name, // This should be vendor email
      subject: `Work Order ${workOrder.work_order_number}`,
      message: ''
    };
    
    // TODO: Open email dialog component
    this.apiService.sendWorkOrderEmail(workOrder.work_order_id, emailData).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Email sent successfully');
          this.loadWorkOrders();
        }
      },
      error: (err: any) => {
        console.error('Error sending email:', err);
        this.toastrService.error('Failed to send email');
      }
    });
  }

  clearFilters() {
    this.searchKeyword = '';
    this.selectedStatus = '';
    this.selectedProject = '';
    this.pageIndex = 0;
    this.loadWorkOrders();
  }
}

